import { StateCreator } from 'zustand/vanilla';

export interface ReadMessagesState {
  readMessageIdsByUserScope: Record<string, number[]>;
  steeringHubVisitedByUserScope: Record<string, boolean>;
}

export interface ReadMessagesActions {
  isMessageRead: (accountNumber: string | undefined, userId: string | undefined, messageId: number) => boolean;
  markAsRead: (accountNumber: string | undefined, userId: string | undefined, messageId: number) => void;
  getUnreadMessageCount: (accountNumber: string | undefined, userId: string | undefined) => number;
  markAllMessagesAsRead: (accountNumber: string | undefined, userId: string | undefined, messageIds: number[]) => void;
  markSteeringHubVisited: (accountNumber: string | undefined, userId: string | undefined) => void;
}

export interface ReadMessagesSlice extends ReadMessagesState, ReadMessagesActions {}

const buildUserScopeKey = (accountNumber: string | undefined, userId: string | undefined): string => {
  const accountKey = accountNumber?.trim() || 'default';
  const userKey = userId?.trim() || 'anonymous';
  return `${accountKey}:${userKey}`;
};

export const createReadMessagesSlice: StateCreator<ReadMessagesSlice, [], [], ReadMessagesSlice> = (set, get) => ({
  readMessageIdsByUserScope: {},
  steeringHubVisitedByUserScope: {},

  isMessageRead: (accountNumber: string | undefined, userId: string | undefined, messageId: number) => {
    const scopeKey = buildUserScopeKey(accountNumber, userId);
    return (get().readMessageIdsByUserScope[scopeKey] ?? []).includes(messageId);
  },

  markAsRead: (accountNumber: string | undefined, userId: string | undefined, messageId: number) => {
    const scopeKey = buildUserScopeKey(accountNumber, userId);
    const { readMessageIdsByUserScope } = get();
    const currentReadIds = readMessageIdsByUserScope[scopeKey] ?? [];

    if (currentReadIds.includes(messageId)) return;

    set({
      readMessageIdsByUserScope: {
        ...readMessageIdsByUserScope,
        [scopeKey]: [...currentReadIds, messageId],
      },
    });
  },

  getUnreadMessageCount: (accountNumber: string | undefined, userId: string | undefined) => {
    const scopeKey = buildUserScopeKey(accountNumber, userId);
    const readIds = get().readMessageIdsByUserScope[scopeKey] ?? [];
    return readIds.length;
  },

  markAllMessagesAsRead: (accountNumber: string | undefined, userId: string | undefined, messageIds: number[]) => {
    const scopeKey = buildUserScopeKey(accountNumber, userId);
    const { readMessageIdsByUserScope } = get();
    const currentReadIds = readMessageIdsByUserScope[scopeKey] ?? [];

    const allReadIds = Array.from(new Set([...currentReadIds, ...messageIds]));

    set({
      readMessageIdsByUserScope: {
        ...readMessageIdsByUserScope,
        [scopeKey]: allReadIds,
      },
    });
  },

  markSteeringHubVisited: (accountNumber: string | undefined, userId: string | undefined) => {
    const scopeKey = buildUserScopeKey(accountNumber, userId);
    const { steeringHubVisitedByUserScope } = get();

    set({
      steeringHubVisitedByUserScope: {
        ...steeringHubVisitedByUserScope,
        [scopeKey]: true,
      },
    });
  },
});
